import { useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Dropdown from './Dropdown';
import { useGetPlatformsQuery } from '@/store/api/platformApi';
import { setSelectedPlatformId } from '@/store/slices/platformSlice';
import { RootState } from '@/store';

const ALL_VALUE = 'ALL';
const ALL_LABEL = 'All';

export default function PlatformSwitcher() {
  const dispatch = useDispatch();
  const { data, isLoading, isError } = useGetPlatformsQuery();
  const platforms = data?.platforms;
  const isInitialized = useRef(false);

  const selectedId = useSelector((s: RootState) => s.platform.selectedPlatformId);

  useEffect(() => {
    if (isLoading) return; // Fallback to ALL if error or no platforms returned

    if (isError || !platforms || platforms.length === 0) {
      if (selectedId !== ALL_VALUE) {
        dispatch(setSelectedPlatformId(ALL_VALUE));
      }
      return;
    } // Default to the first platform got from the platform API

    if (!isInitialized.current) {
      isInitialized.current = true;
      const exists = platforms.some((p) => String(p.id) === selectedId && selectedId !== ALL_VALUE);
      if (!exists) {
        dispatch(setSelectedPlatformId(String(platforms[0]?.id)));
      }
    } else if (selectedId !== ALL_VALUE) {
      const exists = platforms.some((p) => String(p.id) === selectedId);
      if (!exists) {
        dispatch(setSelectedPlatformId(String(platforms[0]?.id)));
      }
    }
  }, [isLoading, isError, platforms, selectedId, dispatch]);

  const filterOptions = useMemo(() => {
    return [
      { value: ALL_VALUE, label: ALL_LABEL },
      ...(platforms || []).map((p) => ({ value: String(p.id), label: p.name })),
    ];
  }, [platforms]);

  const handleFilterChange = (value: string) => {
    dispatch(setSelectedPlatformId(value));
  };

  return (
    <Dropdown
      title="Platform"
      filterOptions={filterOptions}
      defaultValue={selectedId}
      onFilterChange={handleFilterChange}
    />
  );
}
